import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Textarea } from '@/components/ui/Textarea'
import { NoticeSendConfirmModal } from '@/components/venue/NoticeSendConfirmModal'
import { NoticeTargetPicker } from '@/components/venue/NoticeTargetPicker'
import { useCampaignEstimate } from '@/hooks/useCampaignEstimate'
import { useCourts } from '@/hooks/useCourts'
import {
  estimateParamsReady,
  hasComposeFormErrors,
  initialComposeFormState,
  targetCountText,
  toEstimateParams,
  toggleComposeCourt,
  validateComposeForm,
  type ComposeFormErrors,
  type ComposeFormState,
} from '@/lib/venue/campaignViewModel'
import { useVenueContext } from '@/routes/venueContext'

const TITLE_MAX_LENGTH = 100
const BODY_MAX_LENGTH = 1000

/** 요구사항 "발송 안내(무료/운영 정보 목적/발송 후 수정·취소 불가/신규 홍보 목적 사용 불가)".
 * 🚨 "발송 시간 제한" 항목은 뺐다 — #728 §8이 "무료 운영 공지는 발송 시간대 제한의 대상이 아니다"를
 * 확정했다(설계 §자율 결정 6). 없는 제약을 안내하지 않는다. */
const SEND_GUIDE_LINES = [
  '운영 공지는 무료로 발송됩니다.',
  '휴무·예약 변경 등 운영 정보 전달 목적으로만 사용해 주세요.',
  '발송 후에는 내용을 수정하거나 취소할 수 없습니다.',
  '신규 홍보·광고 목적으로는 사용할 수 없습니다(홍보는 지역 홍보 메뉴를 이용해 주세요).',
]

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

/**
 * 공지 작성 — 'notices/new'(설계 §2). 대상 선택·제목·내용·미리보기 + 발송 확인 모달 진입.
 * 🚨 이 화면은 조립만 한다. 검증·대상 조립은 `campaignViewModel.ts`가 소유한다.
 */
export function VenueNoticeComposePage() {
  const { venueId } = useVenueContext()
  const navigate = useNavigate()

  const courtsQuery = useCourts(venueId)
  const courts = courtsQuery.data?.courts.map((court) => ({ courtId: court.courtId, name: court.name })) ?? []

  const [form, setForm] = useState<ComposeFormState>(initialComposeFormState)
  const [errors, setErrors] = useState<ComposeFormErrors>({})
  const [confirmOpen, setConfirmOpen] = useState(false)

  const estimateQuery = useCampaignEstimate(venueId, toEstimateParams(form), estimateParamsReady(form))

  function updateField<K extends keyof ComposeFormState>(key: K, value: ComposeFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  function handleSubmit() {
    const validationErrors = validateComposeForm(form)
    if (hasComposeFormErrors(validationErrors)) {
      setErrors(validationErrors)
      return
    }
    setErrors({})
    setConfirmOpen(true)
  }

  return (
    <>
      <PageHeader title="공지 작성" description="팔로워 또는 특정 예약자에게 운영 공지를 무료로 발송합니다." />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="내용 작성" />
          <CardBody className="space-y-4">
            <NoticeTargetPicker
              form={form}
              errors={errors}
              courts={courts}
              onChangeTargetType={(targetType) => {
                updateField('targetType', targetType)
              }}
              onChangeField={(key, value) => {
                updateField(key, value)
              }}
              onChangeApplyScope={(applyScope) => {
                setForm((current) => ({ ...current, applyScope, courtIds: [] }))
              }}
              onToggleCourt={(courtId) => {
                setForm((current) => toggleComposeCourt(current, courtId))
              }}
            />

            <p className="text-sm text-ink-700">
              대상 수:{' '}
              {estimateQuery.isLoading
                ? '확인 중…'
                : estimateQuery.data
                  ? targetCountText(estimateQuery.data.targetCount)
                  : '-'}
            </p>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-sm font-medium text-ink-700" htmlFor="notice-title">
                  제목
                </label>
                <span className="text-xs text-slateish-400">
                  {form.title.length}/{TITLE_MAX_LENGTH}
                </span>
              </div>
              <Input
                id="notice-title"
                value={form.title}
                maxLength={TITLE_MAX_LENGTH}
                aria-invalid={Boolean(errors.title)}
                placeholder="예) 8월 3일 휴무 안내"
                onChange={(event) => {
                  updateField('title', event.target.value)
                }}
              />
              <FieldError message={errors.title} />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-sm font-medium text-ink-700" htmlFor="notice-body">
                  내용
                </label>
                <span className="text-xs text-slateish-400">
                  {form.body.length}/{BODY_MAX_LENGTH}
                </span>
              </div>
              <Textarea
                id="notice-body"
                rows={5}
                value={form.body}
                maxLength={BODY_MAX_LENGTH}
                aria-invalid={Boolean(errors.body)}
                placeholder="예) 시설 점검으로 8월 3일 휴무입니다."
                onChange={(event) => {
                  updateField('body', event.target.value)
                }}
              />
              <FieldError message={errors.body} />
            </div>
          </CardBody>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title="미리보기" description="실제 앱 표시와 다를 수 있습니다." />
            <CardBody>
              <div className="rounded-lg border border-slateish-200 p-3">
                <p className="text-sm font-semibold text-ink-700">{form.title || '제목을 입력해 주세요'}</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slateish-600">
                  {form.body || '내용을 입력해 주세요'}
                </p>
              </div>
            </CardBody>
          </Card>

          <Alert tone="info" title="발송 안내">
            <ul className="list-disc space-y-1 pl-4">
              {SEND_GUIDE_LINES.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Alert>

          <div className="flex justify-end">
            <Button type="button" onClick={handleSubmit}>
              발송
            </Button>
          </div>
        </div>
      </div>

      {confirmOpen ? (
        <NoticeSendConfirmModal
          venueId={venueId}
          form={form}
          courts={courts}
          onClose={() => {
            setConfirmOpen(false)
          }}
          onSent={(campaignId) => {
            navigate(`/venues/${venueId}/notices/${campaignId}`)
          }}
        />
      ) : null}
    </>
  )
}
